<template>
  <div class="MultipleStudentAttachmentForm">
    <h2 class="gl_form-title small">
      {{ t('hiring.common.insurance-attach-file.title-student') }}
    </h2>
    <div>
      <QRComponent
        v-if="showQR"
        :value="qrUrl"
        :label="t('copy_qr_label')"
        :isFileAttached="isFileAttached"
        :error="qrError"
        @retry="checkQRDocument"
      >
        <p class="hint-text">
          {{ t('hiring.common.insurance-attach-file.attach-file-hint-generic') }}
        </p>
      </QRComponent>
      <CardInfoComponent
        text-align="left"
        icon="icon-informacion"
      >
        {{ t('hiring.common.insurance-attach-file.proof-of-studies-blue-hint') }}
      </CardInfoComponent>
      <p class="hint-text">
        {{
          t('hiring.common.insurance-attach-file.description-proof', {
            document: 'justificante de estudios'
          })
        }}
      </p>
      <BaseFile
        :key="fileKeyRefreshed"
        id="document"
        v-model="documentFiles"
        :rules="[validationRules.requiredFile]"
        :footer-message="t('hiring.common.insurance-another-company-attach-file.message-file')"
        :disabled="isFileAttached"
        @change="onChange"
        @remove="removeQR"
      />
    </div>
  </div>
</template>
<script lang="ts" setup>
  import { computed, ref, onBeforeMount, onBeforeUnmount, watch } from 'vue'
  import { useI18n } from 'vue-i18n'

  import QRComponent from '@/modules/core/components/ui/QRComponent.vue'
  import BaseFile from '@/modules/core/components/ui/BaseFile.vue'
  import { useIsMobile } from '@/modules/core/composables/useIsMobile'
  import { useRules } from '@/modules/core/composables/useRulesComposable'
  import { useCommonStore } from '@/modules/core/stores/commonStore'
  import { useLoadingStore } from '@/modules/core/stores/loadingStore'
  import { useGTMStore } from '@/modules/core/stores/gtmStore'

  import ForeignHealthInsured from '@/modules/foreign-health/hiring/models/MID/ForeignHealthInsured'
  import GTMEventType from '@/modules/core/models/GTMEventType'
  import SpanishBranches from '@/modules/core/models/SpanishBranches'
  import CardInfoComponent from '@/modules/common/common/components/panels/CardInfoComponent.vue'

  interface PropsInterface {
    nextForm: String[]
    answers: Record<string, any>
    configurations: Record<string, any>
    extra?: Record<string, any>
    type?: string
  }
  defineExpose({ triggerSubmit })
  const emit = defineEmits(['update:validateForm'])
  const { requiredFile } = useRules()
  const { isMobile } = useIsMobile()

  const commonStore = useCommonStore()
  const loadingStore = useLoadingStore()
  const gtmStore = useGTMStore()

  const props = withDefaults(defineProps<PropsInterface>(), {})
  const product = props.answers.product

  const { t } = useI18n()

  const validationRules = {
    requiredFile
  }
  const currentInsured = new ForeignHealthInsured(
    props.extra?.customInsuredProp
      ? props.answers[props.extra.customInsuredProp]
      : props.answers.currentInsured
  )

  const isFileAttached = computed(() => documentFiles.value.length > 0)

  const showQR = computed(() => {
    return !isMobile.value
  })

  const qrUrl = ref(generateURl())
  const qrError = ref(false)
  const isCheckingQR = ref(false)

  const fileKeyRefreshed = ref(0)
  const currentInsuredDocument = currentInsured.extraDocument
  const file = currentInsuredDocument?.file
  const documentFiles = ref<File[]>(file ? [file] : [])

  watch(documentFiles, () => {
    fileKeyRefreshed.value += 1
  })

  function triggerSubmit() {
    const page = props.nextForm[0]

    const file = documentFiles.value[0]

    currentInsured.extraDocument!.file = file
    currentInsured.extraDocument!.fileName = file?.name?.replace(/\s/g, '') ?? 'document'

    const answers = props.extra?.customInsuredProp
      ? { [props.extra.customInsuredProp]: currentInsured }
      : props.extra?.currentInsuredIndex === 'holder'
      ? { holder: currentInsured }
      : currentInsured

    return { page, answers }
  }

  async function loadSavedFile() {
    const documentId = currentInsuredDocument?.documentId
    if (currentInsuredDocument?.file || !documentId) return

    const file = await commonStore.fetchDocument(
      documentId,
      currentInsuredDocument.fileName!,
      currentInsuredDocument.mimeType!
    )
    if (!file) return

    documentFiles.value = [file]
    fileKeyRefreshed.value = 1
  }

  function launchGTM() {
    gtmStore.sendEvent({
      event: GTMEventType.CARGA_ARCHIVO,
      tipo_incidencia: undefined,
      tipo_ramo: props.type
        ? SpanishBranches[props.type as keyof typeof SpanishBranches]
        : undefined,
      motivo_baja: undefined
    })
  }

  function generateURl() {
    // 'http://localhost:5173/tarificador/documento/W29iamVjdCBPYmplY3Rd'
    const hash = btoa(
      `${props.answers.quotationId}||${product.abroadOption}||${currentInsured.riskNumber}||MultipleAttachmentQRForm`
    )
    const url = `${window.origin}/tarificador/documento/${hash}`
    console.log(url)
    return url
  }

  async function checkQRDocument() {
    try {
      if (isCheckingQR.value) return
      isCheckingQR.value = true
      if (documentFiles.value.length > 0) return
      const result = await commonStore.getQRName(
        props.answers.quotationId,
        props.extra?.useCurrentInsured ? currentInsured.riskNumber : 0
      )
      if (result) {
        const qrFile = await commonStore.getQRFile(
          props.answers.quotationId,
          props.extra?.useCurrentInsured ? currentInsured.riskNumber : 0
        )
        if (!qrFile) return
        documentFiles.value = [qrFile]
        return
      }
      setTimeout(async () => await checkQRDocument(), 1000)
    } catch (error) {
      qrError.value = true
    } finally {
      isCheckingQR.value = false
    }
  }

  async function removeQR() {
    await commonStore.deleteQR(props.answers.quotationId)
    checkQRDocument()
  }

  function onChange() {
    launchGTM()
    emit('update:validateForm')
  }

  async function beforeMount() {
    loadingStore.startLoading()
    await commonStore.deleteQR(props.answers.quotationId)
    await loadSavedFile()
    loadingStore.stopLoading()
    if (!isFileAttached.value && showQR.value) {
      checkQRDocument()
    }
  }

  onBeforeMount(beforeMount)
  onBeforeUnmount(() => (isCheckingQR.value = true))
</script>
<style lang="scss" scoped>
  .MultipleStudentAttachmentForm {
    .hint-text {
      margin-top: $spacing-l;
      margin-bottom: $spacing-l;
      color: $color-neutral;
      font-size: $font-size-m;
      font-weight: $font-weight-400;
      line-height: $line-height-140p;
    }
  }
</style>
